import React from "react";
import {
  Tab,
  TabList,
  Text,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from "@fluentui/react-components";
import { MoreHorizontalRegular } from "@fluentui/react-icons/svg/more-horizontal";
import { useTranslation } from "react-i18next";
import type { Source } from "@onetasklist/shared";
import { useCatalogueViewWithBillingViewModel } from "./CatalogueView.ViewModel";
import { CatalogueSourceCard } from "./CatalogueSourceCard";
import { CatalogueSuggestSourceCard } from "./CatalogueSuggestSourceCard";

// ─── Overflow helpers ─────────────────────────────────────────

const CatalogueOverflowMenuItem: React.FC<{
  id: string;
  label: string;
  onClick: () => void;
}> = ({ id, label, onClick }) => {
  const isVisible = useIsOverflowItemVisible(id);
  if (isVisible) return null;
  return <MenuItem onClick={onClick}>{label}</MenuItem>;
};

const CatalogueOverflowMenu: React.FC<{
  categories: string[];
  getLabel: (cat: string) => string;
  onSelect: (cat: string) => void;
}> = ({ categories, getLabel, onSelect }) => {
  const { ref, isOverflowing, overflowCount } =
    useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing) return null;

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          ref={ref}
          appearance="transparent"
          className="catalogue-view__overflow-btn"
          icon={<MoreHorizontalRegular />}
          aria-label={`${overflowCount} more categories`}
          role="tab"
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {categories.map((cat) => (
            <CatalogueOverflowMenuItem
              key={cat}
              id={cat}
              label={getLabel(cat)}
              onClick={() => onSelect(cat)}
            />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

interface CatalogueViewProps {
  allSources: Source[];
  searchText: string;
  isConnecting: boolean;
  hasVotedForSource: (sourceId: string) => boolean;
  onSelectSource: (source: Source) => void;
  onVoteForSource: (sourceId: string) => void;
  onOpenSourcePrivacy: (sourceId: string) => void;
  onSuggestSource: () => void;
}

export const CatalogueView: React.FC<CatalogueViewProps> = ({
  allSources,
  searchText,
  isConnecting,
  hasVotedForSource,
  onSelectSource,
  onVoteForSource,
  onOpenSourcePrivacy,
  onSuggestSource,
}) => {
  const { t } = useTranslation();
  const vm = useCatalogueViewWithBillingViewModel(allSources, searchText);

  const tCategory = (cat: string): string => {
    if (cat === "All") return t("board.connections.catalogue.allCategories");
    const key = cat
      .toLowerCase()
      .replace(/\s*&\s*/g, "_and_")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    return t(`board.connections.catalogue.categories.${key}`, {
      defaultValue: cat,
    });
  };

  return (
    <div className="catalogue-view">
      <Overflow minimumVisible={2}>
        <TabList
          selectedValue={vm.selectedCategory}
          onTabSelect={(_, data) => vm.setSelectedCategory(data.value as string)}
          className="catalogue-view__tabs"
        >
          {vm.categories.map((cat) => (
            <OverflowItem
              key={cat}
              id={cat}
              priority={cat === vm.selectedCategory ? 2 : 1}
            >
              <Tab value={cat}>{tCategory(cat)}</Tab>
            </OverflowItem>
          ))}
          <CatalogueOverflowMenu
            categories={vm.categories}
            getLabel={tCategory}
            onSelect={vm.setSelectedCategory}
          />
        </TabList>
      </Overflow>

      {vm.availableSources.length > 0 && (
        <section className="catalogue-view__section">
          <Text weight="semibold" className="catalogue-view__section-title">
            {t("board.connections.catalogue.availableSection", {
              count: vm.availableSources.length,
            })}
          </Text>
          <div className="catalogue-view__grid">
            {vm.availableSources.map((source) => (
              <CatalogueSourceCard
                key={source.id}
                source={source}
                isConnecting={isConnecting}
                hasVoted={hasVotedForSource(source.id)}
                billingBadge={vm.getBillingBadge(source)}
                onConnect={() => onSelectSource(source)}
                onVote={() => onVoteForSource(source.id)}
                onOpenPrivacy={() => onOpenSourcePrivacy(source.id)}
              />
            ))}
          </div>
        </section>
      )}

      {vm.plannedSources.length > 0 && (
        <section className="catalogue-view__section">
          <Text weight="semibold" className="catalogue-view__section-title">
            {t("board.connections.catalogue.plannedSection", {
              count: vm.plannedSources.length,
            })}
          </Text>
          <div className="catalogue-view__grid">
            {vm.plannedSources.map((source) => (
              <CatalogueSourceCard
                key={source.id}
                source={source}
                isConnecting={isConnecting}
                hasVoted={hasVotedForSource(source.id)}
                billingBadge={vm.getBillingBadge(source)}
                onConnect={() => onSelectSource(source)}
                onVote={() => onVoteForSource(source.id)}
                onOpenPrivacy={() => onOpenSourcePrivacy(source.id)}
              />
            ))}
            <CatalogueSuggestSourceCard onClick={onSuggestSource} />
          </div>
        </section>
      )}

      {vm.availableSources.length === 0 && vm.plannedSources.length === 0 && (
        <Text className="catalogue-view__empty">
          {t("board.connections.sources.noResults")}
        </Text>
      )}
    </div>
  );
};
